import { useEffect, useRef, useState } from "react";
import { useSevaGuide } from "./useSevaGuide";

const SUGGESTED = [
  "What does AGM do?",
  "How does my donation help?",
  "Explain Vedant and seva.",
  "Show healthcare programs.",
  "I want to volunteer.",
  "Add me to the newsletter.",
];

const LEAD_CONFIG = {
  newsletter: {
    heading: "Get AGM updates by email",
    button: "Subscribe",
    showMessage: false,
    messageLabel: "",
  },
  volunteer: {
    heading: "Volunteer with AGM",
    button: "Count me in",
    showMessage: true,
    messageLabel: "How would you like to help? (optional)",
  },
  inquiry: {
    heading: "Connect with the AGM team",
    button: "Send details",
    showMessage: true,
    messageLabel: "Your message (optional)",
  },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function LeadForm({ kind, onSubmit, onDismiss }) {
  const cfg = LEAD_CONFIG[kind] || LEAD_CONFIG.inquiry;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Please enter your name.");
      return;
    }
    if (!EMAIL_RE.test(email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!consent) {
      setError("Please tick consent so the AGM team can follow up.");
      return;
    }
    setError("");
    setSubmitting(true);
    const res = await onSubmit({
      name: name.trim(),
      email: email.trim(),
      message: message.trim(),
      consent: true,
    });
    setSubmitting(false);
    if (!res?.ok) {
      setError(res?.message || "Something went wrong. Please try the contact form.");
    }
  };

  return (
    <form
      onSubmit={handle}
      noValidate
      className="mt-2 space-y-2 rounded-xl border border-[#e7d6c8] bg-white p-3"
      aria-label={cfg.heading}
    >
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#9d1f1c]">
        {cfg.heading}
      </p>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Your name"
        aria-label="Your name"
        className="w-full rounded-lg border border-[#d8c7ba] bg-white px-3 py-1.5 text-sm outline-none focus:border-[#9d1f1c] focus-visible:ring-2 focus-visible:ring-[#9d1f1c]/30"
      />
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Your email"
        aria-label="Your email"
        className="w-full rounded-lg border border-[#d8c7ba] bg-white px-3 py-1.5 text-sm outline-none focus:border-[#9d1f1c] focus-visible:ring-2 focus-visible:ring-[#9d1f1c]/30"
      />
      {cfg.showMessage && (
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={cfg.messageLabel}
          aria-label={cfg.messageLabel}
          rows={2}
          className="w-full resize-none rounded-lg border border-[#d8c7ba] bg-white px-3 py-1.5 text-sm outline-none focus:border-[#9d1f1c] focus-visible:ring-2 focus-visible:ring-[#9d1f1c]/30"
        />
      )}
      <label className="flex items-start gap-2 text-[11px] leading-tight text-[#5f4a3e]">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          aria-label="Consent to be contacted"
          className="mt-0.5"
        />
        <span>I agree the AGM team may contact me about this. AGM never sells your data.</span>
      </label>
      {error && <p className="text-[11px] font-medium text-[#9d1f1c]">{error}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={submitting}
          className="rounded-full bg-[#9d1f1c] px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#86251e] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f5c45e] focus-visible:ring-offset-1 disabled:opacity-60"
        >
          {submitting ? "Sending…" : cfg.button}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-full border border-[#d8c7ba] px-4 py-1.5 text-xs font-semibold text-[#5f4a3e] transition-colors hover:border-[#9d1f1c] hover:text-[#9d1f1c] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9d1f1c]"
        >
          Not now
        </button>
      </div>
    </form>
  );
}

export default function SevaGuide() {
  const [openPanel, setOpenPanel] = useState(false);
  const [input, setInput] = useState("");
  const [leadHandled, setLeadHandled] = useState(() => new Set());
  const sourcePage = typeof window !== "undefined" ? window.location.pathname : "/";
  const { messages, busy, send, submitLead } = useSevaGuide(sourcePage);
  const endRef = useRef(null);

  // Close on Escape key — keyboard a11y
  useEffect(() => {
    if (!openPanel) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenPanel(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openPanel]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ behavior: "smooth" });
  }, [messages, openPanel]);

  const submit = (e) => {
    e?.preventDefault?.();
    if (!input.trim()) return;
    send(input);
    setInput("");
  };

  return (
    <>
      {!openPanel && (
        <button
          type="button"
          aria-label="Ask AGM"
          onClick={() => setOpenPanel(true)}
          className="fixed bottom-5 right-5 z-[60] flex items-center gap-2 rounded-full bg-[#9d1f1c] px-5 py-3 font-semibold text-white shadow-xl shadow-[#241511]/30 transition-colors duration-200 hover:bg-[#86251e] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f5c45e] focus-visible:ring-offset-2"
        >
          <span aria-hidden="true" className="inline-block h-2 w-2 rounded-full bg-[#f5c45e]" />
          Ask AGM
        </button>
      )}

      {openPanel && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="AGM Seva Guide"
          className="fixed bottom-5 right-5 z-[60] flex h-[560px] w-[min(92vw,400px)] flex-col overflow-hidden rounded-2xl border border-[#d8c7ba] bg-[#fffaf5] shadow-2xl shadow-[#241511]/30 transition-all duration-300"
        >
          {/* Header */}
          <header className="flex items-center justify-between bg-[#241511] px-4 py-3 text-white">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#f5c45e]">
                AvdheshanandG Mission
              </div>
              <div className="font-semibold">AGM Seva Guide</div>
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={() => setOpenPanel(false)}
              className="rounded-full px-2 py-0.5 text-xl leading-none text-white/80 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f5c45e]"
            >
              ×
            </button>
          </header>

          {/* Messages */}
          <div className="flex-1 space-y-3 overflow-auto p-4">
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "text-right" : "text-left"}>
                <div
                  className={`inline-block max-w-[88%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#9d1f1c] text-white"
                      : "border border-[#e7d6c8] bg-white text-[#241511]"
                  }`}
                >
                  {m.text}
                </div>
                {m.actions?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {m.actions.map((a, j) => (
                      <a
                        key={j}
                        href={a.href}
                        className="rounded-full border border-[#9d1f1c] px-3 py-1 text-xs font-semibold text-[#9d1f1c] transition-colors hover:bg-[#9d1f1c] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9d1f1c] focus-visible:ring-offset-1"
                      >
                        {a.label}
                      </a>
                    ))}
                  </div>
                )}
                {m.role === "bot" &&
                  m.leadPrompt &&
                  LEAD_CONFIG[m.leadPrompt] &&
                  !leadHandled.has(i) && (
                    <LeadForm
                      kind={m.leadPrompt}
                      onSubmit={async (fields) => {
                        const res = await submitLead({ kind: m.leadPrompt, fields });
                        if (res?.ok) setLeadHandled((s) => new Set(s).add(i));
                        return res;
                      }}
                      onDismiss={() => setLeadHandled((s) => new Set(s).add(i))}
                    />
                  )}
              </div>
            ))}

            {/* Suggested questions — only when there is only the initial greeting */}
            {messages.length <= 1 && (
              <div className="space-y-2 pt-2">
                {SUGGESTED.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => send(q)}
                    className="block w-full rounded-lg border border-[#e7d6c8] bg-white px-3 py-2 text-left text-sm text-[#241511] transition-colors hover:border-[#9d1f1c] hover:bg-[#fffaf5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9d1f1c]"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {busy && (
              <div aria-live="polite" className="text-left text-xs text-[#9d1f1c]">
                AGM Seva Guide is typing…
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <form onSubmit={submit} className="border-t border-[#e7d6c8] bg-[#fbf5ef] p-3">
            <div className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about AGM in English or Hindi…"
                aria-label="Ask AGM a question"
                className="flex-1 rounded-full border border-[#d8c7ba] bg-white px-4 py-2 text-sm outline-none transition-colors focus:border-[#9d1f1c] focus-visible:ring-2 focus-visible:ring-[#9d1f1c]/30"
              />
              <button
                type="submit"
                aria-label="Send"
                disabled={busy}
                className="rounded-full bg-[#9d1f1c] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#86251e] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f5c45e] focus-visible:ring-offset-2 disabled:opacity-60"
              >
                Send
              </button>
            </div>
            <p className="mt-2 text-[10px] leading-tight text-[#7a6a5d]">
              Answers use AGM's published content. We store this chat to improve support; please
              don't share sensitive personal data.
            </p>
          </form>
        </div>
      )}
    </>
  );
}
